/**
 * packages/dados/src/cliente.ts — a única porta de acesso ao Supabase.
 *
 * ESTE CÓDIGO RODA NO NAVEGADOR. Tudo que estiver aqui é público: qualquer usuário
 * abre o DevTools e lê. Por isso:
 *
 *   1. Nenhuma chave é embutida no código. A configuração é INJETADA por quem monta
 *      o app (`criarCliente({ url, chavePublicavel })`). O módulo também não lê
 *      variável de ambiente por conta própria — `process.env` / `import.meta.env`
 *      são responsabilidade da camada que faz o build (Vite `define`, `/config.json`
 *      buscado no boot, binding do Worker). Assim o mesmo bundle serve dev e produção,
 *      e este pacote continua testável sem ambiente.
 *   2. A chave SECRETA (`sb_secret_…` / `service_role`) NUNCA entra aqui — ela ignora
 *      todas as políticas de RLS. `criarCliente` recusa explicitamente uma chave
 *      dessas: é o erro que a gente prefere ter às 3 da tarde no dev, e não às 3 da
 *      manhã em produção. Ver R-02 no PLANO-Fatia1.
 *
 * ESQUEMA: todo o modelo vive em `app` (não existe `public` neste banco), então o
 * cliente já nasce apontado para `app`. Para a trilha de auditoria, use
 * `obterCliente().schema('audit')` — também tipado.
 *
 * Quem decide o que cada um pode ler ou escrever é o BANCO (RLS + políticas). Este
 * cliente usa a chave publicável e o JWT do usuário; ele não tem — e não deve ter —
 * nenhum poder além do que a política concede.
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './banco.tipos';

/** Esquema padrão das consultas. Não existe `public` neste banco. */
export const ESQUEMA = 'app';

/**
 * Prazo de cada ida ao servidor.
 *
 * 30 s é deliberadamente GENEROSO. O objetivo é acabar com o "pendurado por minutos",
 * não cortar operação lenta: uma publicação com muitas unidades pode levar segundos, e
 * cortar cedo criaria o pior estado possível — a escrita comita no servidor e o cliente
 * anuncia falha. Com 30 s isso é remoto; se acontecer, o erro é classificado como queda
 * (portanto "tente de novo"), e uma nova publicação bate na trava otimista e informa que
 * alguém já publicou, em vez de sobrescrever.
 *
 * ⚠ RESÍDUO CONHECIDO: para ESCRITA, um prazo estourado não diz se o servidor comitou.
 * Reconciliar isso (reler a versão e concluir se a gravação valeu) é trabalho à parte,
 * fora deste lote.
 */
export const PRAZO_PADRAO_MS = 30_000;

/** O cliente tipado do projeto. Use este alias, nunca `SupabaseClient` cru. */
export type ClienteFincast = SupabaseClient<Database, 'app'>;

/** Configuração injetada por quem monta o app. Só valor público entra aqui. */
export interface ConfigSupabase {
  /** URL do projeto, ex.: `https://xxxx.supabase.co`. */
  url: string;
  /** Chave **publicável** (a antiga `anon`). Nunca a `secret`/`service_role`. */
  chavePublicavel: string;
  /**
   * Chave do armazenamento local da sessão. Trocar isola ambientes que dividem o
   * mesmo domínio (dev x homologação) para um não derrubar a sessão do outro.
   */
  chaveDeArmazenamento?: string;
  /**
   * Prazo de cada ida ao servidor, em milissegundos. Padrão: PRAZO_PADRAO_MS (30 s).
   * Zero ou negativo desliga — só para teste; em produção sem prazo a tela pendura.
   */
  prazoMs?: number;
  /** Manter a sessão entre recargas da página. Padrão: true. */
  persistirSessao?: boolean;
  /** Renovar o token antes de expirar. Padrão: true. */
  autoRenovarToken?: boolean;
  /** Ler o token do link de convite/recuperação ao abrir a página. Padrão: true. */
  detectarSessaoNaUrl?: boolean;
  /**
   * Fluxo de autenticação. Padrão `pkce`: o token não trafega no fragmento da URL
   * (não vai parar no histórico do navegador nem em `Referer`). ⚠ os modelos de
   * e-mail do projeto Supabase precisam ser os compatíveis com PKCE — se o link de
   * recuperação chegar com `#access_token=`, passe `'implicit'`.
   */
  fluxoAuth?: 'pkce' | 'implicit';
  /** Cabeçalhos extras em toda requisição (ex.: identificação de versão do app). */
  cabecalhos?: Record<string, string>;
}

/**
 * Erro do pacote de dados. `codigo` é estável (dá para ramificar no código);
 * `message` é PT-BR e pode ir direto para a tela.
 */
export class ErroFincast extends Error {
  readonly codigo: string;
  readonly causa: unknown;
  constructor(codigo: string, mensagem: string, causa?: unknown) {
    super(mensagem);
    this.name = 'ErroFincast';
    this.codigo = codigo;
    this.causa = causa;
  }
}

/* ============================================================================
 * Guarda contra chave secreta
 * ========================================================================== */

/** Decodifica a carga de um JWT sem validar assinatura (só para inspeção local). */
function cargaDoJwt(parte: string): { role?: unknown } | null {
  try {
    const b64 = parte.replace(/-/g, '+').replace(/_/g, '/');
    const completo = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    if (typeof atob !== 'function') return null;
    const objeto: unknown = JSON.parse(atob(completo));
    return typeof objeto === 'object' && objeto !== null ? (objeto as { role?: unknown }) : null;
  } catch {
    return null; // chave que não é JWT: não é motivo para bloquear
  }
}

/**
 * Devolve a descrição do problema quando a chave NÃO é publicável, ou `null` quando
 * está tudo bem. Falha fechado no que reconhece e falha aberto no que não reconhece —
 * é uma rede de proteção contra erro de configuração, não um controle de segurança.
 */
function chaveProibida(chave: string): string | null {
  const c = chave.trim();
  if (c === '') return 'a chave está vazia';
  if (c.startsWith('sb_secret_')) return 'esta é a chave SECRET (sb_secret_…)';
  if (c.startsWith('sbp_')) return 'este é um token pessoal da conta Supabase (sbp_…)';
  if (c.startsWith('eyJ')) {
    const partes = c.split('.');
    if (partes.length === 3 && cargaDoJwt(partes[1] ?? '')?.role === 'service_role') {
      return 'esta é a chave SERVICE_ROLE (JWT com role=service_role)';
    }
  }
  return null;
}

function validarConfig(config: ConfigSupabase): void {
  let url: URL;
  try {
    url = new URL(config.url);
  } catch {
    throw new ErroFincast(
      'CONFIG_URL_INVALIDA',
      `URL do Supabase inválida: ${JSON.stringify(config.url)}. Esperado algo como https://xxxx.supabase.co`,
    );
  }
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1';
  if (url.protocol !== 'https:' && !local) {
    throw new ErroFincast(
      'CONFIG_URL_INSEGURA',
      `A URL do Supabase precisa ser https (recebido ${url.protocol}//). Sem TLS o token do usuário trafega em claro.`,
    );
  }
  const problema = chaveProibida(config.chavePublicavel);
  if (problema !== null) {
    throw new ErroFincast(
      'CHAVE_SECRETA_NO_NAVEGADOR',
      `Chave recusada: ${problema}. Este código roda no navegador — use a chave PUBLICÁVEL (anon/publishable). ` +
        'A chave secreta ignora todas as regras de permissão do banco e não pode sair do servidor. ' +
        'Se ela já foi colada em algum arquivo do front, revogue-a no painel do Supabase.',
    );
  }
}

/* ============================================================================
 * Ciclo de vida (uma instância por página)
 * ========================================================================== */

let cliente: ClienteFincast | null = null;
let assinaturaAtual = '';

function assinaturaDe(config: ConfigSupabase): string {
  // sem a chave: a assinatura pode aparecer em mensagem de erro
  return `${config.url}|${config.chaveDeArmazenamento ?? 'fincast.auth'}|${config.fluxoAuth ?? 'pkce'}`;
}

/**
 * Cria o cliente (uma vez) e o devolve. Chamar de novo com a MESMA configuração
 * devolve a mesma instância; com configuração diferente é erro — duas instâncias
 * dividindo o mesmo armazenamento brigam pela renovação do token e derrubam a
 * sessão do usuário de forma intermitente (o clássico "Multiple GoTrueClient
 * instances detected").
 */
/**
 * O relógio do serviço que EMITE o token e o do serviço que o VALIDA não são o mesmo.
 *
 * Quando o segundo está uma fração de segundo atrás do primeiro, um token recém-criado
 * chega com `iat` (emitido em) no futuro e é recusado com **401 "JWT issued at future"**.
 * Acontece na janela de milissegundos logo depois de entrar — que é exatamente quando a
 * tela faz a primeira leitura do perfil. O efeito para o usuário seria entrar com a senha
 * certa e cair numa tela de erro.
 *
 * Foi observado três vezes durante a validação e nunca reproduziu duas vezes seguidas —
 * o que é a assinatura do problema, não motivo para ignorá-lo: some sozinho porque a
 * requisição seguinte já acontece alguns milissegundos depois.
 *
 * Uma retentativa, uma vez só, e apenas para ESTE erro. Repetir 401 em geral seria
 * perigoso: mascararia sessão expirada e permissão negada, que precisam chegar à tela.
 */
const ESPERA_RELOGIO_MS = 400;

async function comRetentativaDeRelogio(chamar: () => Promise<Response>): Promise<Response> {
  const r = await chamar();
  if (r.status !== 401) return r;

  /* `clone()` porque o corpo só pode ser lido uma vez: sem isto, quem chamou receberia
     uma resposta já drenada e veria "erro desconhecido" no lugar da mensagem real. */
  let corpo = '';
  try {
    corpo = await r.clone().text();
  } catch {
    return r;
  }
  if (!/issued at future|JWSInvalidSignature|token used before issued/i.test(corpo)) return r;

  await new Promise((ok) => setTimeout(ok, ESPERA_RELOGIO_MS));
  return chamar();
}

export function criarCliente(config: ConfigSupabase): ClienteFincast {
  const assinatura = assinaturaDe(config);
  if (cliente !== null) {
    if (assinatura !== assinaturaAtual) {
      throw new ErroFincast(
        'CLIENTE_JA_CRIADO',
        'O cliente do Supabase já foi criado com outra configuração. Crie um só por página.',
      );
    }
    return cliente;
  }
  validarConfig(config);

  cliente = createClient<Database, 'app'>(config.url, config.chavePublicavel, {
    db: { schema: ESQUEMA },
    auth: {
      storageKey: config.chaveDeArmazenamento ?? 'fincast.auth',
      persistSession: config.persistirSessao ?? true,
      autoRefreshToken: config.autoRenovarToken ?? true,
      detectSessionInUrl: config.detectarSessaoNaUrl ?? true,
      flowType: config.fluxoAuth ?? 'pkce',
    },
    global: {
      headers: { 'x-aplicacao': 'fincast', ...config.cabecalhos },
      /* PRAZO em UM lugar só.
         Antes, nenhuma ida ao servidor tinha prazo: uma consulta que nunca respondia
         deixava a barra em "Salvando…" por MINUTOS, até a pilha TCP do sistema matar o
         socket, e a promessa ficava pendurada — a tela a espera antes de publicar ou
         fechar. Pôr aqui, no `fetch`, cobre leitura, escrita e RPC de uma vez, em vez de
         espalhar `.abortSignal()` por dezenas de consultas.
         O prazo é generoso de propósito (ver `prazoMs`). */
      /* `Parameters<typeof fetch>` em vez de `RequestInfo`: o tsconfig não carrega a
         lib "DOM" (este pacote também roda em Node, nos testes). */
      fetch: (entrada: Parameters<typeof fetch>[0], iniciais?: Parameters<typeof fetch>[1]) => {
        const prazo = config.prazoMs ?? PRAZO_PADRAO_MS;
        const comPrazo: Parameters<typeof fetch>[1] =
          prazo <= 0 || iniciais?.signal
            /* Se quem chamou já trouxe o próprio sinal, ele manda — não atropelamos
               cancelamento pedido de fora. */
            ? iniciais
            : { ...iniciais, signal: AbortSignal.timeout(prazo) };
        return comRetentativaDeRelogio(() => fetch(entrada, comPrazo));
      },
    },
  });
  assinaturaAtual = assinatura;
  return cliente;
}

/**
 * Devolve o cliente já criado. Falha com mensagem clara se `criarCliente` não foi
 * chamado — o sintoma sem isso seria um `undefined` genérico três camadas acima.
 */
export function obterCliente(): ClienteFincast {
  if (cliente === null) {
    throw new ErroFincast(
      'CLIENTE_NAO_INICIALIZADO',
      'O cliente do Supabase ainda não foi criado. Chame criarCliente({ url, chavePublicavel }) ' +
        'uma vez no início do app (antes de qualquer tela) e só então use obterCliente().',
    );
  }
  return cliente;
}

/**
 * Descarta a instância. Existe para TESTE (cada caso monta o seu cliente) e para o
 * caso raro de trocar de projeto sem recarregar a página. Não use em fluxo normal:
 * quem já guardou a referência antiga continua com ela.
 */
export function redefinirClienteParaTestes(): void {
  cliente = null;
  assinaturaAtual = '';
}
